import * as React from 'react';
import {
  Body,
  Button,
  Input,
  LoadingIcon,
  Modal,
  Toggle,
  Tooltip,
} from 'vet-bones/design-system/components';
import { useDidValueChange } from 'vet-bones/utils/hooks';
import { isCloud } from 'vet-bones/utils/portal';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  ADD_ENDPOINT_DIALOG_CANCEL,
  ADD_ENDPOINT_DIALOG_ENDPOINT,
  ADD_ENDPOINT_DIALOG_INTERNAL_ENDPOINT,
  ADD_ENDPOINT_DIALOG_NAME,
  ADD_ENDPOINT_DIALOG_PASSWORD,
  ADD_ENDPOINT_DIALOG_SUBMIT,
  ADD_ENDPOINT_DIALOG_USERNAME,
  ADD_ENDPOINT_DIALOG_USE_SSO,
} from 'src/ui/constants/testIds';
import { useInvalidateConnections } from 'src/ui/containers/endpoints/utils/invalidate';
import { ConnectionStatus, clearConnection } from 'src/ui/features/connection';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useAddConnectionMutation } from 'src/ui/graph/types';
import { useProfile, useVerifyConnection } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const isOpenSelector = isDialogOpenSelector(DialogType.ADD_ENDPOINT);

export const AddEndpointDialog: React.VFC<{ isLaunchpad?: boolean }> = ({
  isLaunchpad,
}) => {
  const dispatch = useAppDispatch();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const isOpen = useAppSelector(isOpenSelector);
  const didIsOpenChange = useDidValueChange(isOpen);

  const { data: profile } = useProfile();
  const invalidateConnections = useInvalidateConnections('');
  const {
    isLoading: isValidating,
    mutate: validate,
    reset: resetValidation,
  } = useVerifyConnection();
  const {
    isLoading: isAdding,
    mutate: addConnection,
  } = useAddConnectionMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: () => {
      invalidateConnections();
      dispatch(closedDialog());
      dispatch(clearConnection());
    },
  });

  const [endpoint, setEndpoint] = React.useState('');
  const [internalEndpoint, setInternalEndpoint] = React.useState('');
  const [name, setName] = React.useState('');
  const [useSSO, setUseSSO] = React.useState(false);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');

  React.useEffect(() => {
    if (!didIsOpenChange || !isOpen) {
      return;
    }

    setEndpoint('');
    setInternalEndpoint('');
    setName('');
    setUseSSO(false);
    setUsername('');
    setPassword('');
  }, [didIsOpenChange, isOpen]);

  const handleClose = () => {
    dispatch(closedDialog());
    resetValidation();
    setEndpoint('');
    setInternalEndpoint('');
    setName('');
    setUseSSO(false);
    setUsername('');
    setPassword('');
  };

  const handleSubmit = () => {
    const trimmedEndpoint = endpoint.trim();
    const trimmedName = name.trim();
    const trimmedUsername = username.trim();

    if (
      !trimmedEndpoint ||
      !trimmedName ||
      (trimmedUsername && !password) ||
      (!trimmedUsername && password)
    ) {
      return;
    }

    // Test connection with `newPassword`, it is important that we remove the
    // existing token if there is one because the Stardog.js client will prefer
    // using that and we'll just get a 401 making the user super mad and throw
    // stuff. We don't want to be responsible for broken screens or coffee mugs.
    const newConnection = {
      endpoint: trimmedEndpoint,
      token: undefined,
      username: trimmedUsername,
      password,
      useSSO,
      name: trimmedName,
      status: ConnectionStatus.DISCONNECTED,
    };

    validate(newConnection, {
      onError,
      onSuccess: (data) => {
        invalidateConnections();
        addConnection({
          input: {
            endpoint: trimmedEndpoint,
            internalEndpoint: internalEndpoint.trim() || undefined,
            name: trimmedName,
            token: data.token,
            username: trimmedUsername,
          },
        });
      },
    });
  };

  let submitTooltip = '';
  if (!name.trim()) {
    submitTooltip = copy.components.endpoints.inputs.name.validationRequired;
  } else if (!endpoint.trim()) {
    submitTooltip =
      copy.components.endpoints.inputs.endpoint.validationRequired;
  } else if (username && !password) {
    submitTooltip =
      copy.components.endpoints.inputs.password.validationRequired;
  } else if (!username && password) {
    submitTooltip =
      copy.components.endpoints.inputs.username.validationRequired;
  }

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={ADD_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.endpoints.addEndpointDialog.buttons.cancel}
          </Button>
          <Tooltip description={submitTooltip} disabled={!submitTooltip}>
            <Button
              disabled={isValidating || isAdding || Boolean(submitTooltip)}
              leftIcon={isValidating || isAdding ? LoadingIcon : undefined}
              onClick={handleSubmit}
              size="sm"
              testId={ADD_ENDPOINT_DIALOG_SUBMIT}
              variant="primary-accent"
            >
              {copy.components.endpoints.addEndpointDialog.buttons.submit}
            </Button>
          </Tooltip>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.endpoints.addEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <form
          className="endpoint-dialog-form"
          onSubmit={(evt) => {
            evt.preventDefault();
            handleSubmit();
          }}
        >
          <Input
            autoFocus
            clearable={false}
            id="sd-connection-name"
            label={copy.components.endpoints.inputs.name.label}
            onChange={(e) => setName(e.target.value)}
            required
            size="md"
            testId={ADD_ENDPOINT_DIALOG_NAME}
            value={name}
          />

          <Input
            clearable={false}
            id="sd-connection-endpoint"
            label={copy.components.endpoints.inputs.endpoint.label}
            onChange={(e) => setEndpoint(e.target.value)}
            required
            size="md"
            testId={ADD_ENDPOINT_DIALOG_ENDPOINT}
            value={endpoint}
          />

          {isCloud() && profile?.profile?.is_legacy_customer_sso ? (
            <Toggle
              align="right"
              checked={useSSO}
              className="endpoint-dialog-toggle"
              id="sd-connection-use-sso"
              onChange={() => setUseSSO(!useSSO)}
              size="md"
              testId={ADD_ENDPOINT_DIALOG_USE_SSO}
            >
              {copy.components.endpoints.inputs.useSSO.label}
            </Toggle>
          ) : null}

          {
            // only show username/password for non-sso connections
            useSSO ? null : (
              <>
                <Input
                  autoComplete="username"
                  clearable={false}
                  id="sd-connection-username"
                  label={copy.components.endpoints.inputs.username.label}
                  onChange={(e) => setUsername(e.target.value)}
                  size="md"
                  testId={ADD_ENDPOINT_DIALOG_USERNAME}
                  value={username}
                />

                <Input
                  autoComplete="password"
                  clearable={false}
                  id="sd-connection-password"
                  label={copy.components.endpoints.inputs.password.label}
                  onChange={(e) => setPassword(e.target.value)}
                  size="md"
                  testId={ADD_ENDPOINT_DIALOG_PASSWORD}
                  type="password"
                  value={password}
                />
              </>
            )
          }

          {isLaunchpad ? (
            <>
              <div className="endpoint-dialog-divider" />

              <div className="endpoint-dialog-container">
                <Input
                  clearable={false}
                  id="sd-connection-internal-endpoint"
                  label={
                    copy.components.endpoints.inputs.internalEndpoint.label
                  }
                  onChange={(e) => setInternalEndpoint(e.target.value)}
                  placeholder={
                    copy.components.endpoints.inputs.internalEndpoint
                      .placeholder
                  }
                  size="md"
                  testId={ADD_ENDPOINT_DIALOG_INTERNAL_ENDPOINT}
                  value={internalEndpoint}
                />
                <Body className="endpoint-dialog-input-footer" size="sm">
                  {copy.components.endpoints.inputs.internalEndpoint.help}
                </Body>
              </div>
            </>
          ) : null}

          <input hidden type="submit" />
        </form>
      </div>
    </Modal>
  );
};
